'use client'
import React, { useState, useEffect, createContext, useContext } from "react";
import { CheckCircle, XCircle, X, AlertCircle, Info } from "lucide-react";

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: number;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showWarning: (message: string) => void;
    showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

const ToastNotification: React.FC<{ 
    toast: Toast; 
    onClose: (id: number) => void 
}> = ({ toast, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose(toast.id);
        }, 5000);
        return () => clearTimeout(timer);
    }, [toast.id, onClose]);

    const styles = {
        success: {
            container: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
            icon: 'text-green-600 dark:text-green-400',
            text: 'text-green-800 dark:text-green-200',
            button: 'hover:bg-green-600 text-green-600 dark:text-green-400'
        },
        error: {
            container: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
            icon: 'text-red-600 dark:text-red-400',
            text: 'text-red-800 dark:text-red-200',
            button: 'hover:bg-red-600 text-red-600 dark:text-red-400'
        },
        warning: {
            container: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
            icon: 'text-yellow-600 dark:text-yellow-400',
            text: 'text-yellow-800 dark:text-yellow-200',
            button: 'hover:bg-yellow-600 text-yellow-600 dark:text-yellow-400'
        },
        info: {
            container: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-primary',
            icon: 'text-blue-600 dark:text-blue-400',
            text: 'text-primary dark:text-blue-200',
            button: 'hover:bg-blue-600 text-blue-600 dark:text-blue-400'
        }
    };

    const style = styles[toast.type];

    const Icon = {
        success: CheckCircle,
        error: XCircle,
        warning: AlertCircle,
        info: Info
    }[toast.type];

    return (
        <div className={`flex items-center gap-3 p-4 rounded-lg shadow-lg border ${style.container} animate-slide-in`}>
            <Icon className={`w-5 h-5 ${style.icon} flex-shrink-0`} />
            <p className={`flex-1 text-sm font-medium ${style.text}`}>
                {toast.message}
            </p>
            <button
                onClick={() => onClose(toast.id)}
                className={`flex-shrink-0 rounded-md p-1 hover:bg-opacity-20 transition ${style.button}`}
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

const ToastContainer: React.FC<{ toasts: Toast[]; onClose: (id: number) => void }> = ({ 
    toasts, 
    onClose 
}) => {
    return (
        <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 max-w-md">
            {toasts.map(toast => (
                <ToastNotification key={toast.id} toast={toast} onClose={onClose} />
            ))}
        </div>
    );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = (message: string, type: ToastType = 'info') => {
        const id = Date.now();
        setToasts([{ id, message, type }]);  // replace instead of spread
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const contextValue: ToastContextType = {
        showToast,
        showSuccess: (message: string) => showToast(message, 'success'),
        showError: (message: string) => showToast(message, 'error'),
        showWarning: (message: string) => showToast(message, 'warning'),
        showInfo: (message: string) => showToast(message, 'info'),
    };

    return (
        <ToastContext.Provider value={contextValue}>
            <ToastContainer toasts={toasts} onClose={removeToast} />
            {children}
        </ToastContext.Provider>
    );
};
export default ToastProvider;